import React from 'react';
import _ from 'underscore';

import {
  InlineEditorLogic,
  isEditMode,
  TagsEditor,
  TagsEditorState,
  useInlineEditor
} from '@tableau/vizportalclient-common-components';
import { TextLinkWidget } from '@tableau/widgets';

import { Messages } from '~src/codegen/Localize';
import { SearchSuggestionsService } from '~src/content/services/SearchSuggestionsService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { ListResult } from '~src/ts/fetchers/ListResult';
import { ITag } from '~src/ts/ServerApi/types/ITag';

import { ACTION_DIALOG_ID } from './ContentDetailsDialog';
import { ContentDetailSection, DetailEditButton, DetailSaveCancelButtons } from './ContentDetailSection';


// TFS 1195698 requires some limitations on tagging. This is a quick attempt to
// have a limit even if no users are likely to hit it. A more robust fix will
// be thought through and added to the backend, at which point we might be able
// to take the front-end checks out.
const DEFAULT_TAG_CHANGE_LIMIT: number = 1000;

// ID for accessibilty purposes
const sectionHeaderId = ACTION_DIALOG_ID+'-Tags';

export interface TagsDetailSectionProps {
  readonly tags?: string[];
  readonly isActionEnabled: boolean;
  readonly setTags: (tags: string[]) => Promise<IResult>;
  readonly tagUrlFn?: (tag: string) => string;
  readonly incrementRouterSpaceReactKey: () => void;
  readonly changeLimit?: number;  // exposed for testing
}

export function TagsDetailSection(props: TagsDetailSectionProps): JSX.Element {

  const handleFilterItems = (partialText: string) => {
    return (
      SearchSuggestionsService.tags(partialText)
        .getSlice(0, 10)
        .then((result: ListResult<ITag>) => {
          return result.items.map((value: ITag) => value.name);
        })
    );
  };

  const onSave = React.useCallback(async (editorState: TagsEditorState) => {
    await props.setTags([...editorState.tags])
      .then(rejectIfResultContainsError)
      .then(props.incrementRouterSpaceReactKey);
  },[
    props.setTags,
    props.incrementRouterSpaceReactKey
  ]);

  const canSave = (editorState: TagsEditorState) => {
    if(_.isEqual([...editorState.tags].sort(), [...initialEditorState.tags].sort())) {
      return false;
    }

    // rudimentary limit checking for tags, defect 1195698
    const existingNumTags = props.tags?.length ?? 0;
    const modifiedNumTags = editorState?.tags?.length ?? 0;
    const allowedDifference = props.changeLimit ?? DEFAULT_TAG_CHANGE_LIMIT;
    return Math.abs(existingNumTags - modifiedNumTags) <= allowedDifference;
  };

  const initialEditorState: TagsEditorState = {
    tags: props.tags,
    suggestions: [],
    partialText: '',
  };

  const logic: InlineEditorLogic<TagsEditorState> = useInlineEditor<TagsEditorState>({
    editorStateWhenEnteringEditMode: initialEditorState,
    canEdit: props.isActionEnabled,
    onSave: onSave,
    canSaveFn: canSave,
  });

  if (isEditMode(logic)) {
    return (
      <ContentDetailSection
        header = {Messages.column_tags()}
        testId = {'tags'}
        headerId={sectionHeaderId}
        detail = {
          <TagsEditor
            aria-labelledby={sectionHeaderId}
            editorState={logic.editorState}
            setEditorState={logic.setEditorStateWithinEditMode}
            searchMatchingTags={handleFilterItems}
            disableEditor={logic.isActionBusy}
          />
        }
        isActionAllowed={true}
        action = {
          <DetailSaveCancelButtons
            inlineEditorLogic={logic}
          />
        }
      />
    );
  }
  else {
    return (
      <ContentDetailSection
        header = {Messages.column_tags()}
        testId = {'tags'}
        detail = {
          <TagsReadingMode
            displayedTags = {props.tags}
            tagUrlFn = {props.tagUrlFn}
          />
        }
        isActionAllowed={logic.canEdit}
        action = {
          <DetailEditButton
            buttonLabel={Messages.editTags()}
            buttonTestId={'edit-tags'}
            onClickAction={logic.enterEditMode}
          />
        }
      />
    );
  }
}

export interface TagsReadingModeProps {
  readonly displayedTags: string[];
  readonly tagUrlFn?: (tag: string) => string;
}

export function TagsReadingMode(props: TagsReadingModeProps): JSX.Element {
  const [tagLinks, setTagLinks] = React.useState<any[]>([]);

  React.useEffect(() => {
    var processedLinks: any[] = [];

    for (let index = 0; index < props.displayedTags?.length; index++) {
      const tagName = props.displayedTags[index];
      const tagLink = props.tagUrlFn ? props.tagUrlFn(tagName) : '';

      if(index > 0) {
        processedLinks.push(<span key={`comma${index}`}>, </span>);
      }

      if(tagLink === '') {
        processedLinks.push(<span key={tagName}>{tagName}</span>);
      } else {
        processedLinks.push(
          <TextLinkWidget
            testId="tag"
            href={tagLink}
            children={tagName}
            key={tagName}
          />);
      }
    }

    setTagLinks(processedLinks);
  }, []);

  if(tagLinks.length === 0) {
    return null;
  } else {
    return (
      <div>{ tagLinks }</div>
    );
  }
}
